<template>
    <div>
        <v-container grid-list-xs>
            <v-row>
                <v-col>
                    <v-sheet class="pa-5" elevation="3">
                        <PageHeader>
                            <template #header>
                                <h4>{{ $t("plan.review-selection-header") }}</h4>
                            </template>
                            <template #subheader>
                                {{ $t(`plan.review-selection-subheader`) }}
                            </template>
                            <template #main>
                                <div class="mt-6">{{ $t("loan-plan-review.selected-offer-title") }}</div>
                            </template>
                        </PageHeader>

                        <v-radio-group hide-details :value="paymentPlan">
                            <PaymentPlanOption
                                v-if="paymentPlan"
                                :key="paymentPlan.providerPlanCode"
                                :payment-plan="paymentPlan"
                                :down-payment-required="downPaymentRequired"
                                :financed-amount-cents="tentativePurchaseAmountCents"
                                :provider-key="paymentPlan.providerKey"
                            />
                        </v-radio-group>

                        <div>
                            <div id="loan-additional-info" class="mt-8">
                                <div class="font-weight-bold">
                                    {{ $t("loan-plan-review.treatment-amount-label") }}
                                    <FormattedAmount :amount="tentativePurchaseAmountCents" :include-cents="true" />
                                </div>
                                <div class="font-weight-bold">
                                    {{ $t("loan-plan-review.financed-amount-label") }}
                                    <FormattedAmount :amount="financedAmountCents" :include-cents="true" />
                                </div>
                                <div class="text-body-2">{{ $t("loan-plan-review.financed-amount-footnote") }}</div>
                            </div>

                            <div id="loan-body">
                                <div class="font-weight-bold">
                                    {{ $t("loan-plan-review.edit-amount-instruction") }}
                                </div>
                                <div>
                                    <ul>
                                        <li>
                                            {{ $t("loan-plan-review.amount-range-prefix") }}
                                            <FormattedAmount :amount="minAmountCents" /> -
                                            <FormattedAmount :amount="maxAmountCents" />
                                            {{ $t("loan-plan-review.amount-range-suffix") }}
                                        </li>
                                        <li v-if="initialPayment">
                                            {{
                                                $t("loan-plan-review.initial-payment-required", {
                                                    downPaymentPercent: paymentPlan.downPaymentPercent,
                                                })
                                            }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div>
                            {{ $t("loan-plan-review.continue-to-synchrony-site") }}
                        </div>

                        <div class="card-actions pb-12 mt-8">
                            <v-btn class="card-actions-back" rounded outlined color="primary" @click="back()">
                                {{ $t("action.back") }}
                            </v-btn>
                            <SubmitButton
                                id="continueButton"
                                class="card-actions-submit secondary"
                                @click="submit"
                            >
                                {{ continueButtonVerbiage }}
                            </SubmitButton>
                        </div>
                    </v-sheet>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent } from "vue"
    import { applicationState, providerState, sessionState } from "@/state"
    import PaymentPlanOption from "@/components/paymentplan/PaymentPlanOption.vue"
    import ApplyMode from "@/model/apply/ApplyMode"
    import router from "@/router"
    import { i18n } from "@/plugins/i18n"

    export default defineComponent({
        components: {
            PaymentPlanOption,
        },

        setup() {
            const application = applicationState.application
            const paymentPlan = applicationState.selectedPaymentPlan

            let response = null
            if (applicationState.applyMode !== ApplyMode.FULL) {
                response = providerState.getPreAuthResponse()
            }
            if (applicationState.applyMode === ApplyMode.FULL) {
                response = providerState.getApplyResponse()
            }

            const downPaymentRequired = response?.downPaymentRequired
            const financedAmountCents = paymentPlan?.financedAmountCents
            const maxAmountCents = paymentPlan?.maxAmountCents
            const minAmountCents = paymentPlan?.minAmountCents
            const tentativePurchaseAmountCents = application.tentativePurchaseAmountCents

            const continueButtonVerbiage = computed(() => {
                if (applicationState.applyMode === ApplyMode.FULL) return i18n.t("action.continue-and-finalize")
                return i18n.t("action.continue-to-apply")
            })

            const initialPayment = computed(() => {
                return paymentPlan?.downPaymentCents !== undefined || paymentPlan?.downPaymentPercent !== undefined
            })

            const back = () => {
                applicationState.selectedPaymentPlan = null
                router.push(`/providers/${sessionState.providerKey}/paymentPlans`)
            }

            const submit = async () => {
                await router.push(`/providers/${sessionState.providerKey}/payMonthlyLoader`)
            }

            return {
                continueButtonVerbiage,
                downPaymentRequired,
                financedAmountCents,
                initialPayment,
                maxAmountCents,
                minAmountCents,
                paymentPlan,
                tentativePurchaseAmountCents,
                back,
                submit,
            }
        },
    })
</script>

<style lang="scss" scoped>
    #loan-additional-info {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin-bottom: 20px;
    }

    p {
        color: $primary-color;
    }

    td {
        max-width: 250px;
    }

    table tr {
        border: 1px solid red !important;
    }

    .table-font {
        font-size: to_rem(16px) !important;
    }

    .v-data-table td {
        border-bottom: 1px lightgrey solid;
        padding: 10px 10px 10px 24px !important;

        &:first-child {
            width: 40%;
        }

        &:nth-child(1) {
            border-bottom: none;
        }

        &:last-child {
            border-bottom: none;
        }
    }

    .no-left-border {
        border-left: none !important;
    }

    .no-right-border {
        border-right: none !important;
    }

    .v-input__control {
        margin-right: -10px !important;
    }

    .payment-container {
        background-color: $bg-color-neutral;
        border-radius: 8px;
        margin-top: 20px;
        padding-left: 20px;
    }

    .payment-container-nobg {
        margin-top: 20px;
        padding-left: 20px;
    }

    @media only screen and (max-width: 600px) {
        .v-data-table td {
            &:first-child {
                width: 60%;
            }

            &:nth-child(2) {
                text-align: left;
            }
        }
    }
    .cost-box {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        text-align: left;
        padding: 5px;
        height: auto;
        background-color: rgba(231, 242, 254);
        border-radius: 8px;
        border: 1px solid #002855;
        margin-top: 1rem;
        .tool-tip {
            width: 100%;
            padding-right: 10px;
        }

        .banner-text {
            margin-top: 1rem;
            margin-left: 0;
            font-size: 16px;
            line-height: 10px;
        }
    }
</style>
